import { httpPost, httpDownload } from '../../../../utils/api.utils';
import { DEFAULT_TABLE_RESULT_MODEL, WIRELINE_REQUEST_TYPES_ID, ORDER_HISTORY_OBJ as orderHistoryObj } from '../../../../constants/external-constants';
import { getLocalTimeZone, convertToFormatLocalDate, convertToDecimal, convertToFormatTimeZoneDate } from '../../../../utils/helpers.utils';

import {
    setOrderHistoryLogsLoading
    , getOrderHistoryLogs
    , setOrderHistoryPageSize
    , resetOrderHistory
    , setOrderHistoryColSearchStr
} from '../../../../constants/model-reducers/external/order/history/order-history.settings';

const defaultState = () => ({
    HistoryTable: DEFAULT_TABLE_RESULT_MODEL,
    IsLoadingTable: false
});

const initialState = defaultState();

// Mirrors the headers the API builds from HistoryLogModel; used when the history request fails so the table still shows its columns
const defaultHistoryHeaders = () => [
    ['Event', 'Event'],
    ['Object', 'Object'],
    ['OldValue', 'Old Value'],
    ['NewValue', 'New Value'],
    ['User', 'User'],
    ['Date', 'Date']
].reduce((headers, [propName, name], order) => ({
    ...headers,
    [propName]: {
        propName,
        name,
        hidden: false,
        isSorted: false,
        isSortedAsc: false,
        isDateTime: false,
        searchString: '',
        order,
        shortName: ''
    }
}), {});

export const orderHistoryActions = {

    getOrderHistoryLogs: (OrderId, RequestTypeId, QuotationRequestId, pageIndex = 0) => async (dispatch, state) => {
        const { Pagination, Headers } = state().orderHistory.HistoryTable;
        var formData = {
            OrderID: OrderId,
            PageIndex: pageIndex,
            PageSize: Pagination.PerPage,
            PageSizes: Pagination.PageSizes,
            Headers: Headers,
            QuotationRequestID: QuotationRequestId
        }; 
        
        dispatch({   
            type: setOrderHistoryLogsLoading,
            isLoadingTable: true         
        });

        const emptyHistoryTable = () => ({
            ...DEFAULT_TABLE_RESULT_MODEL,
            Headers: Headers && Object.keys(Headers).length > 0 ? Headers : defaultHistoryHeaders(),
            Pagination: { ...Pagination, PageNumber: 0, TotalEntries: 0 },
            Data: { ...DEFAULT_TABLE_RESULT_MODEL.Data, Items: [] }
        });

        let result;
        try {
            if (RequestTypeId === WIRELINE_REQUEST_TYPES_ID.newQuotation) {
                result = await httpPost('external/order/getNewQuotationHistoryLogs', formData);
            }
            else {
                result = await httpPost('external/order/getOrderHistoryLogs', formData);
            }
        } catch (error) {
            console.error('Error fetching order history logs:', error);
        }

        // Failed request (e.g. 400 from bad log data) returns no Data - show an empty table instead of hanging on loading
        if (!result || !result.Data || !Array.isArray(result.Data.Items)) {
            dispatch({
                type: getOrderHistoryLogs,
                historyTable: emptyHistoryTable(),
                isLoadingTable: false
            });
            return;
        }

        const formattedItems = result.Data.Items.map(item => {
            const formattedItem = { ...item };
            Object.keys(formattedItem).forEach(key => {
                if (key === 'OldValue' || key === 'NewValue') {
                    const objectName = formattedItem['Object'];
                    const value = formattedItem[key];
                    if ((objectName === orderHistoryObj.mrc || objectName === orderHistoryObj.price || objectName === orderHistoryObj.percent) && !isNaN(value)) {
                        if (objectName === orderHistoryObj.percent && value === "") {
                            formattedItem[key] = "";
                        }
                        else {
                            formattedItem[key] = convertToDecimal(value);
                        }                                            
                    }
                }
            });
            return formattedItem;
        });

        dispatch({
            type: getOrderHistoryLogs,
            historyTable: {
             ...result,
            Data: {
                ...result.Data,
                Items: formattedItems,
                },
            },
          
            isLoadingTable: false
        });
    },

    resizeOrderHistoryLogs: (pageSize) => async (dispatch, state) => {
        dispatch({
            type: setOrderHistoryPageSize,
            pageSize: parseInt(pageSize),
        });
    },

    setOrderHistoryColSearchStr: (event, key) => async (dispatch, state) => {
        var val = event.target.value;
        const { HistoryTable } = state().orderHistory;
        var historyTableHeaderUpd = {
            ...HistoryTable,
            "Headers": {
                ...HistoryTable.Headers,
                [key]: {

                    ...HistoryTable.Headers[key],
                    searchString: val
                }
            }
        }

        dispatch({
            type: setOrderHistoryColSearchStr,
            historyTable: historyTableHeaderUpd
        });
    },


    extractOrderHistoryLogs: (OrderId, QuotationRequestId, RequestTypeId) => async (dispatch, state) => {
        const { Headers } = state().orderHistory.HistoryTable;
        const dealName = sessionStorage.getItem('DealName');
        const { activeDateFormat, activeTimeZoneOffset } = state().adminSetting
        const formattedTimeZoneDate = convertToFormatTimeZoneDate(
            new Date(),
            activeDateFormat?.Name || "YYYY-MM-DD",
            activeTimeZoneOffset?.UtcOffset || 0)
            .replace(/[<>:"/\|?*]/g, '-');
        var filename = `ATEM ${dealName} Order History – ${formattedTimeZoneDate}`;
        var formData = {
            OrderID: OrderId,
            QuotationRequestID: QuotationRequestId,
            Headers: Headers,
            FileName: filename,
        };

        const url =
            RequestTypeId === WIRELINE_REQUEST_TYPES_ID.newQuotation
                ? 'external/order/getExtractQuotationRequestHistory'
                : 'external/order/getExtractOrdersHistory';

        const downloadFile = async (url, formData, filename) => {
            try {
                const response = await httpDownload(url, formData);
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');

                if (window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob, filename);
                } else if (typeof a.download === 'undefined') {
                    window.location = downloadUrl;
                } else {
                    a.href = downloadUrl;
                    a.download = `${filename}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                }
            } catch (error) {
                console.error("File download failed:", error);
            }
        };

        await downloadFile(url, formData, filename);
    },


    resetOrderHistory: () => (dispatch, state) => {
        dispatch({
            type: resetOrderHistory
        })
    }
}

export const reducer = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case setOrderHistoryLogsLoading:
            return {
                ...state,
                IsLoadingTable: action.isLoadingTable
            }
        case getOrderHistoryLogs:
            return {
                ...state,
                HistoryTable: action.historyTable,
                IsLoadingTable: action.isLoadingTable
            }
        case setOrderHistoryPageSize:
            return {
                ...state,
                HistoryTable: {
                    ...state.HistoryTable,
                    Pagination: {
                        ...state.HistoryTable.Pagination,
                        PerPage: action.pageSize
                    }
                }
            }
        case setOrderHistoryColSearchStr:
            return {
                ...state,
                HistoryTable: action.historyTable,
            }
        case resetOrderHistory:
            return initialState;
        default:
            return state;
    }
}